import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useBalances, useCardMonthFatura } from '@/hooks/useAccounts'
import { useCurrentRates, convertWithRates } from '@/hooks/useCurrentRates'
import { useHousehold } from '@/context/HouseholdContext'
import { formatCurrency } from '@/lib/currency'
import { ConversionIndicator } from '@/components/ConversionIndicator'
import { accountIcon } from '@/lib/accountIcon'
import { useT } from '@/i18n/I18nProvider'
import type { Account } from '@/types/db'

// Agrupamento do resumo por TIPO de conta (rótulos plurais estilo grupo).
// Os valores são chaves de i18n; o rótulo visível é resolvido com t() na renderização.
const TYPE_LABELS: Record<Account['type'], string> = {
  corrente: 'dash.typeCorrente',
  poupanca: 'dash.typePoupanca',
  cash: 'dash.typeCash',
  carteira: 'dash.typeCarteira',
  investimento: 'dash.typeInvestimento',
  cartao: 'dash.typeCartao',
}
const TYPE_ORDER = [
  'dash.typeCorrente',
  'dash.typePoupanca',
  'dash.typeCash',
  'dash.typeCarteira',
  'dash.typeInvestimento',
  'dash.typeCartao',
]

interface Row {
  account: Account
  displayValue: number // valor na moeda de exibição (own p/ conta; base p/ cartão)
  displayCurrency: string
  valueBase: number | null // valor consolidado em base (p/ subtotais)
  isCard: boolean
}

interface Group {
  name: string
  rows: Row[]
  subtotalBase: number
  hasMissingRate: boolean
}

// Resumo de contas estilo Lunch Money: grupos colapsáveis, subtotal por grupo
// consolidado na moeda base, cada conta na sua própria moeda (cartões = dívida).
export function AccountsOverview() {
  const { t } = useT()
  const { baseCurrency } = useHousehold()
  const { data: balances, isLoading } = useBalances()
  // Régua POR FATURA (byStatement): mostra o que VENCE na fatura do mês — igual à
  // tela Cartões e ao card principal do dashboard. Uma compra feita depois do
  // fechamento já é dívida (régua por data), mas só é COBRADA na fatura seguinte;
  // aqui queremos "fatura do mês", então ela cai no mês em que vence.
  const { data: cardFatura } = useCardMonthFatura(undefined, undefined, true)
  const { data: rates } = useCurrentRates()
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const { groups, anyMissing } = useMemo(() => {
    const map = new Map<string, Row[]>()
    for (const b of balances ?? []) {
      const isCard = b.account.type === 'cartao'
      // Cartão no dashboard = fatura do MÊS ATUAL (dívida ≥ 0 vira negativo);
      // contas usam o saldo corrente. Ignora datas futuras.
      const value = isCard ? -(cardFatura?.get(b.account.id) ?? 0) : b.balance
      const base = convertWithRates(value, b.account.currency, rates)
      const row: Row = {
        account: b.account,
        displayValue: value,
        displayCurrency: b.account.currency,
        valueBase: base,
        isCard,
      }
      const key = TYPE_LABELS[b.account.type]
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(row)
    }

    const groups: Group[] = [...map.entries()]
      .map(([name, rows]) => {
        let subtotalBase = 0
        let hasMissingRate = false
        for (const r of rows) {
          if (r.valueBase == null) hasMissingRate = true
          else subtotalBase += r.valueBase
        }
        // maiores valores no topo (por magnitude na moeda base), menores no fim
        rows.sort((a, b) => Math.abs(b.valueBase ?? 0) - Math.abs(a.valueBase ?? 0) || a.account.name.localeCompare(b.account.name))
        return { name, rows, subtotalBase, hasMissingRate }
      })
      // Ordem fixa por tipo.
      .sort((a, b) => {
        const order = TYPE_ORDER
        const ia = order.indexOf(a.name)
        const ib = order.indexOf(b.name)
        const pa = ia === -1 ? order.length : ia
        const pb = ib === -1 ? order.length : ib
        return pa - pb || a.name.localeCompare(b.name)
      })

    const anyMissing = groups.some((g) => g.hasMissingRate)
    return { groups, anyMissing }
  }, [balances, cardFatura, rates])

  function toggle(name: string) {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t('dash.accountsSummary')}</CardTitle>
        {anyMissing && <span className="text-muted-foreground text-xs">{t('dash.currencyNoRate')}</span>}
      </CardHeader>
      <CardContent className="p-0">
        {/* Espera as TRÊS fontes: sem a fatura ou as taxas, os cartões apareceriam
            zerados/errados por um instante antes de estabilizar. */}
        {isLoading || !balances || !cardFatura || !rates ? (
          <p className="text-muted-foreground p-6">{t('common.loading')}</p>
        ) : groups.length === 0 ? (
          <p className="text-muted-foreground p-6 text-center">{t('dash.emptyAccounts')}</p>
        ) : (
          <div className="divide-border divide-y">
            {groups.map((g) => {
              const isCollapsed = collapsed.has(g.name)
              return (
                <div key={g.name}>
                  <button
                    onClick={() => toggle(g.name)}
                    className="hover:bg-accent/50 flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="text-muted-foreground size-4 shrink-0" />
                    ) : (
                      <ChevronDown className="text-muted-foreground size-4 shrink-0" />
                    )}
                    <span className="flex-1 font-medium">
                      {t(g.name)} <span className="text-muted-foreground font-normal">({g.rows.length})</span>
                    </span>
                    <span
                      className={cn(
                        'tabular-nums',
                        g.subtotalBase < -0.005 ? 'text-destructive' : 'text-foreground',
                      )}
                    >
                      {formatCurrency(g.subtotalBase, baseCurrency)}
                    </span>
                  </button>

                  {!isCollapsed && (
                    <div>
                      {g.rows.map((r) => (
                        <Link
                          key={r.account.id}
                          to={r.isCard ? `/cartoes/${r.account.id}` : `/contas/${r.account.id}`}
                          className="hover:bg-accent/30 flex items-center gap-2 py-2 pr-4 pl-10 text-sm transition-colors"
                        >
                          <span className="flex flex-1 items-center gap-1.5 truncate">
                            <span className="truncate">{accountIcon(r.account)} {r.account.name}</span>
                          </span>
                          <span className="flex items-center gap-1.5 whitespace-nowrap">
                            <ConversionIndicator
                              amount={r.displayValue}
                              currency={r.displayCurrency}
                              baseCurrency={baseCurrency}
                              rates={rates}
                            />
                            <span className={cn('tabular-nums', r.displayValue < -0.005 ? 'text-destructive' : '')}>
                              {formatCurrency(r.displayValue, r.displayCurrency)}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
      {anyMissing && (
        <div className="text-muted-foreground px-4 pb-3 text-xs">
          {t('dash.partialSubtotal')}
        </div>
      )}
    </Card>
  )
}
